import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  ServiceListQueryParams,
  IServiceData,
  ModerationState,
} from '@/types/service-management.types';
import {
  useDeleteServiceMutation,
  useGetServicesQuery,
  useToggleServiceVisibilityMutation,
  useSubmitServiceMutation,
  useWithdrawServiceMutation,
} from '@/services/ServiceManagementService';

const formatCategoryDisplay = (category: string): string => {
  if (!category) return 'N/A';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getModerationStatusBadge = (status?: ModerationState) => {
  const baseClasses =
    'px-3 py-1.5 rounded-full border text-xs font-bold min-w-[110px] inline-block text-center';

  switch (status) {
    case ModerationState.APPROVED:
      return (
        <span
          className={`${baseClasses} bg-emerald-100 dark:bg-emerald-900/20 contrast:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 contrast:text-emerald-400 border-emerald-200 dark:border-emerald-800/40 contrast:border-emerald-800/40`}
        >
          Approved
        </span>
      );
    case ModerationState.REJECTED:
      return (
        <span
          className={`${baseClasses} bg-red-100 dark:bg-red-900/20 contrast:bg-red-900/20 text-red-800 dark:text-red-400 contrast:text-red-400 border-red-200 dark:border-red-800/40 contrast:border-red-800/40`}
        >
          Rejected
        </span>
      );
    case ModerationState.SUBMITTED:
      return (
        <span
          className={`${baseClasses} bg-orange-100 dark:bg-orange-900/20 contrast:bg-orange-900/20 text-orange-800 dark:text-orange-400 contrast:text-orange-400 border-orange-200 dark:border-orange-800/40 contrast:border-orange-800/40`}
        >
          Submitted
        </span>
      );
    case ModerationState.DRAFT:
      return (
        <span
          className={`${baseClasses} bg-gray-100 dark:bg-coal-600 contrast:bg-coal-600 text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 border-gray-200 dark:border-coal-100 contrast:border-coal-100`}
        >
          Draft
        </span>
      );
    case ModerationState.DISABLED:
      return (
        <span
          className={`${baseClasses} bg-gray-100 dark:bg-coal-600 contrast:bg-coal-600 text-gray-700 dark:text-gray-700 contrast:text-gray-900 border-gray-200 dark:border-coal-100 contrast:border-coal-100 opacity-60`}
        >
          Disabled
        </span>
      );
    default:
      return (
        <span
          className={`${baseClasses} bg-gray-100 dark:bg-coal-600 contrast:bg-coal-600 text-gray-700 dark:text-gray-700 contrast:text-gray-900 border-gray-200 dark:border-coal-100 contrast:border-coal-100`}
        >
          N/A
        </span>
      );
  }
};

interface ServiceListingsProps {
  serviceProviderId?: string | null;
  canCreateService?: boolean;
  onCreateService?: () => void;
}

const ServiceListings = ({
  serviceProviderId,
  canCreateService,
  onCreateService,
}: ServiceListingsProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    serviceId: string | null;
  }>({ isOpen: false, serviceId: null });

  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const [deleteService] = useDeleteServiceMutation();
  const [toggleServiceVisibility] = useToggleServiceVisibilityMutation();
  const [submitService] = useSubmitServiceMutation();
  const [withdrawService] = useWithdrawServiceMutation();

  const queryParams: ServiceListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    ...(categoryFilter && { category: categoryFilter }),
    ...(serviceTypeFilter && { service_type: serviceTypeFilter }),
    ...(serviceProviderId && { service_provider_id: serviceProviderId }),
  };

  const {
    data: servicesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetServicesQuery(queryParams);

  useEffect(() => {
    if (servicesResponse) {
      handleTotalPages(servicesResponse.totalPages || 1);
      handleTotalItems(servicesResponse.totalItems || 0);
    }
  }, [servicesResponse, handleTotalPages, handleTotalItems]);

  const filteredData: IServiceData[] = useMemo(() => {
    if (!servicesResponse?.data) return [];
    return servicesResponse.data;
  }, [servicesResponse]);

  const resetFilters = () => {
    if (searchTerm || categoryFilter || serviceTypeFilter) {
      resetSearch();
      setCategoryFilter('');
      setServiceTypeFilter('');
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleOpenService = useCallback(
    (service: IServiceData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        const params = new URLSearchParams();
        if (serviceProviderId) {
          params.append('serviceProviderId', serviceProviderId);
        }
        navigate(`/services/create?${params.toString()}`);
      } else if (service?.id) {
        if (mode === 'view') {
          navigate(`/services/view/${service.id}`);
        } else {
          navigate(`/services/edit/${service.id}`);
        }
      }
    },
    [serviceProviderId, navigate]
  );

  const handleDelete = async () => {
    if (!confirmDeleteState.serviceId) return;
    try {
      await deleteService(confirmDeleteState.serviceId).unwrap();
      enqueueSnackbar('Service deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, serviceId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, serviceId: null });
    }
  };

  const handleSubmit = async (serviceId: string) => {
    try {
      await submitService(serviceId).unwrap();
      enqueueSnackbar('Service submitted for moderation', {
        variant: 'success',
      });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleWithdraw = async (serviceId: string) => {
    try {
      await withdrawService(serviceId).unwrap();
      enqueueSnackbar('Service withdrawn successfully', { variant: 'success' });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleToggleVisibility = async (
    serviceId: string,
    currentVisibility: boolean
  ) => {
    const newVisibility = !currentVisibility;
    try {
      await toggleServiceVisibility({
        serviceId,
        is_visible: newVisibility,
      }).unwrap();
      enqueueSnackbar(
        `Service ${newVisibility ? 'shown' : 'hidden'} successfully`,
        { variant: 'success' }
      );
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IServiceData) => row.name,
        id: 'name',
        header: 'Service Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <button
            type="button"
            onClick={() => handleOpenService(row.original, 'view')}
            className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={`View ${row.original.name}`}
          >
            {row.original.name}
          </button>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IServiceData) => row.service_provider,
        id: 'service_provider',
        header: 'Service Provider',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.service_provider?.display_name as string}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IServiceData) => row.category,
        id: 'category',
        header: 'Category',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <span className="text-gray-800 font-normal">
            {formatCategoryDisplay(row.original.category)}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IServiceData) => row.sub_category,
        id: 'sub_category',
        header: 'Sub Category',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.sub_category || 'N/A'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IServiceData) => row.service_type,
        id: 'service_type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.service_type || 'N/A'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IServiceData) => row.moderation_state,
        id: 'moderation_state',
        header: 'Moderation Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <div className="flex flex-col gap-1">
            {getModerationStatusBadge(row.original.moderation_state)}
            {(row.original.moderation_state === ModerationState.REJECTED ||
              row.original.moderation_state === ModerationState.DISABLED) &&
              row.original.rejection_reason &&
              (() => {
                const fullReason = row.original.rejection_reason as string;
                const maxLength = 50;
                const displayReason =
                  fullReason.length > maxLength
                    ? `${fullReason.slice(0, maxLength)}...`
                    : fullReason;

                return (
                  <span
                    className="text-[10px] text-red-500 italic max-w-[150px]"
                    title={fullReason}
                  >
                    Reason: {displayReason}
                  </span>
                );
              })()}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: IServiceData) => row.public_visibility,
        id: 'visibility',
        header: 'Public Visibility',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                handleToggleVisibility(
                  row.original.id as string,
                  !!row.original.public_visibility
                );
              }}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${row.original.public_visibility
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-gray-300 hover:bg-gray-400'
                }`}
              role="switch"
              disabled={
                row.original.moderation_state !== ModerationState.APPROVED
              }
              aria-checked={!!row.original.public_visibility}
              title={
                row.original.public_visibility
                  ? 'Click to hide'
                  : 'Click to show'
              }
              aria-label={
                row.original.public_visibility ? 'Hide service' : 'Show service'
              }
            >
              <span className="sr-only">Toggle visibility</span>
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition-transform duration-200 ease-in-out ${row.original.public_visibility
                    ? 'translate-x-6'
                    : 'translate-x-0.5'
                  }`}
              />
            </button>
            <span
              className={`text-sm font-medium ${row.original.public_visibility ? 'text-green-400' : 'text-gray-600'}`}
            >
              {row.original.public_visibility ? 'Visible' : 'Hidden'}
            </span>
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceData } }) => (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Actions for ${row.original.name}`}
          >
            {hasPermission('service_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenService(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View service"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="eye"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            )}
            {hasPermission('service_management_update') &&
              row.original.moderation_state !== ModerationState.SUBMITTED && (
                <button
                  type="button"
                  onClick={() => handleOpenService(row.original, 'edit')}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Edit service"
                  aria-label={`Edit ${row.original.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                    aria-hidden="true"
                  />
                </button>
              )}
            {(row.original.moderation_state === ModerationState.DRAFT ||
              row.original.moderation_state === ModerationState.REJECTED) &&
              hasPermission('service_management_update') && (
                <button
                  type="button"
                  onClick={() => handleSubmit(row.original.id as string)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Submit for moderation"
                  aria-label={`Submit ${row.original.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="rocket"
                    className="text-blue-500"
                    aria-hidden="true"
                  />
                </button>
              )}
            {row.original.moderation_state === ModerationState.SUBMITTED &&
              hasPermission('service_management_update') && (
                <button
                  type="button"
                  onClick={() => handleWithdraw(row.original.id as string)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Withdraw submission"
                  aria-label={`Withdraw ${row.original.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="cross-circle"
                    className="text-orange-500"
                    aria-hidden="true"
                  />
                </button>
              )}
            {hasPermission('service_management_delete') &&
              row.original.moderation_state !== ModerationState.SUBMITTED && (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmDeleteState({
                      isOpen: true,
                      serviceId: row.original.id as string,
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Delete service"
                  aria-label={`Delete ${row.original.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-red-500"
                    aria-hidden="true"
                  />
                </button>
              )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenService, hasPermission]
  );

  return (
    <div className="service-listings card card-grid w-full">
      <h1 className="sr-only">Services</h1>
      <div className="card-header service-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Service Listings</h2>
          <span className="text-sm text-gray-500">
            Manage and moderate services
          </span>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-service"
              aria-label="Search services"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-service"
                placeholder="Search services"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search services by name"
                role="searchbox"
              />
            </label>
          </div>
          {(searchTerm || categoryFilter || serviceTypeFilter) && (
            <button
              type="button"
              data-testid="reset-filters"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>

        {canCreateService && onCreateService && (
          <button
            type="button"
            className="btn btn-sm btn-light h-8 sm:h-10 ml-auto"
            onClick={onCreateService}
            aria-label="Add new service"
          >
            Add New Service
          </button>
        )}
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            Loading services...
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="assertive"
          >
            Failed to load services. Please try again.
          </div>
        ) : (
          <div className="service-grid-wrapper">
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={
                !!searchTerm || !!categoryFilter || !!serviceTypeFilter
              }
              aria-label="Service listings table"
            />
          </div>
        )}
        <ConfirmationDialog
          data-testid="confirmation-dialog"
          open={confirmDeleteState.isOpen}
          onClose={() =>
            setConfirmDeleteState({ isOpen: false, serviceId: null })
          }
          onConfirm={handleDelete}
          message="Are you sure you want to delete this service?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        />
      </div>
    </div>
  );
};

export { ServiceListings };
